import type { GuideTrackerClient, InteractionRequest } from './guide-tracker-client';
import type { QueuedInteraction } from './types';

/** Base del backoff de reintento (ms); crece exponencial hasta el tope. */
const RETRY_BASE_MS = 3000;
const RETRY_MAX_DELAY_MS = 30_000;

export interface GuideTrackerQueueConfig {
  client: GuideTrackerClient;
  flushIntervalMs: number;
  batchSize: number;
  maxRetries: number;
  /**
   * Llamado cuando un item se descarta tras agotar `maxRetries`. El
   * plugin puede usarlo para `console.warn` en debug mode. Nunca debe
   * lanzar — si lo hace, se ignora silenciosamente.
   */
  onError?: (error: Error, item: QueuedInteraction) => void;
}

/**
 * Queue dedicada para `POST /v1/guides/:id/interactions`.
 *
 * Cada item es independiente (URL distinto por `guideId`), así que el
 * "batch" en realidad dispara N requests en paralelo. El `batchSize` es
 * el umbral para forzar un flush — no agrupa requests en uno solo.
 *
 * Retry:
 *   - Si `send()` lanza, el item se devuelve al buffer con `attempts++`.
 *   - El timer regular vuelve a intentar en el próximo tick.
 *   - Al alcanzar `maxRetries`, se descarta llamando `onError`.
 *
 * En `pagehide`/`visibilitychange` (hidden) drena el buffer vía
 * `navigator.sendBeacon`. Best effort — no re-encola lo que el beacon
 * rechace porque la página ya se va.
 */
export class GuideTrackerQueue {
  private buffer: QueuedInteraction[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private flushing = false;
  private unloadCleanup: (() => void) | null = null;

  constructor(private readonly config: GuideTrackerQueueConfig) {
    this.startTimer();
    this.installUnloadHandler();
  }

  /** Tamaño actual del buffer (útil para debug/stats en el demo). */
  get size(): number {
    return this.buffer.length;
  }

  /**
   * Encola una interaction. Si el buffer alcanza `batchSize`, dispara
   * `flush()` inmediato.
   */
  enqueue(request: InteractionRequest): void {
    this.buffer.push({
      guideId: request.guideId,
      payload: request.payload,
      attempts: 0,
      enqueuedAt: Date.now(),
    });
    if (this.buffer.length >= this.config.batchSize) {
      void this.flush();
    }
  }

  /**
   * Envía todo el buffer en paralelo. Items que fallan vuelven al buffer
   * si todavía tienen reintentos disponibles. Idempotente con respecto a
   * flushes concurrentes (el segundo es un no-op).
   */
  async flush(): Promise<void> {
    if (this.flushing || this.buffer.length === 0) return;

    // Solo enviamos los items VENCIDOS (nextRetryAt en el pasado). Los que
    // esperan su backoff+jitter tras un fallo quedan en el buffer para un
    // tick futuro — así los reintentos de distintos clientes no se sincronizan.
    const now = Date.now();
    const due = this.buffer.filter((item) => (item.nextRetryAt ?? 0) <= now);
    if (due.length === 0) return;
    this.flushing = true;

    const dueSet = new Set(due);
    this.buffer = this.buffer.filter((item) => !dueSet.has(item));

    const results = await Promise.allSettled(
      due.map((item) => this.config.client.send({ guideId: item.guideId, payload: item.payload })),
    );

    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      const item = due[i];
      if (!result || !item) continue;
      if (result.status === 'fulfilled') continue;

      item.attempts += 1;
      if (item.attempts < this.config.maxRetries) {
        item.nextRetryAt = Date.now() + this.retryDelayMs(item.attempts);
        this.buffer.push(item);
      } else {
        this.safeOnError(result.reason, item);
      }
    }

    this.flushing = false;
  }

  /**
   * Backoff exponencial (base = intervalo de flush) con equal-jitter: mitad
   * fija + mitad aleatoria. Desincroniza los reintentos entre clientes ante
   * una caída del backend.
   */
  private retryDelayMs(attempts: number): number {
    const base = Math.min(RETRY_BASE_MS * 2 ** (attempts - 1), RETRY_MAX_DELAY_MS);
    return base / 2 + Math.random() * (base / 2);
  }

  /**
   * Drena el buffer vía `sendBeacon`. Síncrono, sin retry. Para pagehide.
   */
  flushBeacon(): void {
    if (this.buffer.length === 0) return;
    const batch = this.buffer.splice(0, this.buffer.length);
    for (const item of batch) {
      this.config.client.sendBeacon({ guideId: item.guideId, payload: item.payload });
    }
  }

  destroy(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.unloadCleanup?.();
    this.unloadCleanup = null;
  }

  private startTimer(): void {
    if (typeof window === 'undefined') return;
    this.timer = setInterval(() => {
      void this.flush();
    }, this.config.flushIntervalMs);
  }

  private installUnloadHandler(): void {
    if (typeof window === 'undefined') return;
    const onPagehide = (): void => this.flushBeacon();
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') this.flushBeacon();
    };
    window.addEventListener('pagehide', onPagehide);
    window.addEventListener('visibilitychange', onVisibility);
    this.unloadCleanup = () => {
      window.removeEventListener('pagehide', onPagehide);
      window.removeEventListener('visibilitychange', onVisibility);
    };
  }

  private safeOnError(reason: unknown, item: QueuedInteraction): void {
    if (!this.config.onError) return;
    const error = reason instanceof Error ? reason : new Error(String(reason));
    try {
      this.config.onError(error, item);
    } catch {
      // Un onError roto no debe afectar al resto del flush.
    }
  }
}
